/**
 * KRA Photo Journal – ResizeModal
 *
 * Admin-only modal for resizing a heavy photo set.
 * Steps:
 *   1. Show folder stats (total size, file count) + destructive-action warning
 *   2. Click "Bắt đầu Resize" — cron picks up the job
 *   3. Progress bar; user can close modal (resize keeps running on server)
 *   4. Done/Failed result
 *
 * Design: follows ImportToDriveModal "ready" step pattern.
 */

import React, { useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import type { PhotoRecord } from "@kra/photo-journal-shared";
import { useResize } from "../hooks/useResize.js";

interface ResizeModalProps {
  photo: PhotoRecord;
  onClose: () => void;
}

function formatMB(bytes: number): string {
  return `${(bytes / 1024 / 1024).toFixed(0)} MB`;
}

export function ResizeModal({ photo, onClose }: ResizeModalProps) {
  const { start, cancel, status, starting, error } = useResize();

  const driveLink = photo.adminDriveLink || photo.driveLink;

  const phase = useMemo(() => {
    if (!status) return "confirm" as const;
    if (status.status === "done") return "done" as const;
    if (status.status === "failed" || status.status === "cancelled") return "failed" as const;
    return "progress" as const;
  }, [status]);

  const handleStart = async () => {
    if (!driveLink) return;
    await start(photo.id, driveLink, photo.chatName);
  };

  const progressPercent = status
    ? Math.round(((status.processed + status.failed) / Math.max(status.total, 1)) * 100)
    : 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
      >
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {phase === "done" ? "Resize xong" : phase === "failed" ? "Resize lỗi" : "Resize ảnh"}
          </h2>
          <button
            onClick={onClose}
            aria-label="Đóng"
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Confirm phase */}
        {phase === "confirm" && (
          <>
            <p className="mb-3 text-sm text-gray-600">
              <strong>{photo.chatName}</strong>
            </p>

            {photo.originalTotalSize && (
              <div className="mb-3 rounded-lg bg-gray-50 p-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Dung lượng hiện tại</span>
                  <span className="font-medium">{formatMB(photo.originalTotalSize)}</span>
                </div>
              </div>
            )}

            {/* Warning */}
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <strong>Cảnh báo:</strong> Sẽ ghi đè file gốc trên Drive. EXIF được giữ lại. Không có backup.
            </div>

            {error && (
              <p className="mb-3 text-sm text-red-600">{error}</p>
            )}

            <button
              onClick={handleStart}
              disabled={starting || !driveLink}
              className="w-full rounded-xl bg-[#B76E6E] px-4 py-2.5 text-sm font-medium text-white transition-opacity disabled:opacity-40"
            >
              {starting ? "Đang tạo job..." : "Bắt đầu Resize"}
            </button>
          </>
        )}

        {/* Progress phase */}
        {phase === "progress" && status && (
          <>
            <div className="mb-4 text-sm text-gray-600">
              Đang xử lý <strong>{status.processed}</strong> / {status.total} ảnh...
            </div>

            {/* Progress bar */}
            <div className="mb-3 h-2 overflow-hidden rounded-full bg-gray-200">
              <motion.div
                className="h-full bg-[#B76E6E]"
                initial={{ width: 0 }}
                animate={{ width: `${progressPercent}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>

            <div className="mb-4 flex justify-between text-xs text-gray-400">
              <span>{progressPercent}%</span>
              {status.failed > 0 && (
                <span className="text-red-400">{status.failed} lỗi</span>
              )}
            </div>

            <p className="text-center text-xs text-gray-400">
              Xử lý nền trên server. Bạn có thể đóng modal này.
            </p>

            <button
              onClick={async () => { await cancel(); onClose(); }}
              className="mt-3 w-full rounded-xl border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
            >
              Huỷ resize
            </button>
          </>
        )}

        {/* Done phase */}
        {phase === "done" && status && (
          <>
            <div className="mb-4 rounded-lg bg-green-50 p-4 text-center">
              <div className="text-2xl">✓</div>
              <p className="mt-1 text-sm font-medium text-green-700">
                Đã resize {status.processed} / {status.total} ảnh
              </p>
              <p className="mt-1 text-xs text-green-600">
                Tiết kiệm {status.savedMB} MB
              </p>
            </div>

            <button
              onClick={onClose}
              className="w-full rounded-xl bg-[#B76E6E] px-4 py-2.5 text-sm font-medium text-white"
            >
              Đóng
            </button>
          </>
        )}

        {/* Failed phase */}
        {phase === "failed" && status && (
          <>
            <div className="mb-4 rounded-lg bg-red-50 p-4">
              <p className="text-sm font-medium text-red-700">
                {status.status === "cancelled" ? "Đã huỷ" : "Resize thất bại"}
              </p>
              <p className="mt-1 text-xs text-red-600">
                {status.processed} / {status.total} thành công, {status.failed} lỗi
              </p>
              {Object.entries(status.errors).length > 0 && (
                <div className="mt-2 max-h-24 overflow-y-auto text-xs text-red-500">
                  {Object.entries(status.errors).slice(0, 3).map(([fileId, err]) => (
                    <p key={fileId}>{fileId.slice(0, 8)}...: {err.slice(0, 80)}</p>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={onClose}
              className="w-full rounded-xl border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
            >
              Đóng
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
}
